import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { authAxios } from '@/lib/api';
import { logError } from '@/lib/logger';
import { SEOEditForm, SEOPageSummary } from './SEOPageParts';

const DEFAULT_PAGES = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About Us' },
  { path: '/products', label: 'Products' },
  { path: '/why-choose-us', label: 'Why Choose Us' },
  { path: '/export-markets', label: 'Export Markets' },
  { path: '/blog', label: 'Blog' },
  { path: '/contact', label: 'Contact' },
];
const NEW_KEY = '__new__';
const emptyForm = (path = '') => ({ path, meta_title: '', meta_description: '', keywords: '', canonical_url: '', noindex: false, custom_schema: '' });

export default function SEOSettingsTab({ settings, setSettings }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const settingsMap = Object.fromEntries(settings.map(s => [s.path, s]));
  const allPaths = [...new Set([...DEFAULT_PAGES.map(p => p.path), ...settings.map(s => s.path)])];

  const startEdit = (path) => {
    const existing = settingsMap[path];
    setEditing(existing ? path : NEW_KEY);
    setForm(existing ? { ...emptyForm(), ...existing } : emptyForm(path));
  };

  const save = async () => {
    if (!form.path) return;
    setSaving(true);
    try {
      const { data } = await authAxios.put('/api/seo-settings', form);
      setSettings(prev => (prev.some(s => s.path === data.path) ? prev.map(s => (s.path === data.path ? data : s)) : [...prev, data]));
      setEditing(null);
    } catch {
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (path) => {
    if (!window.confirm(`Delete SEO settings for ${path}?`)) return;
    try {
      await authAxios.delete(`/api/seo-settings${path}`);
      setSettings(prev => prev.filter(s => s.path !== path));
    } catch (e) { logError('Delete SEO settings failed:', e); }
  };

  const formProps = { form, setForm, saving, onSave: save, onCancel: () => setEditing(null) };

  return (
    <div className="space-y-3" data-testid="seo-settings-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-earth/40 font-sans">Edit meta titles, descriptions, keywords, and index settings per page</p>
        <button onClick={() => { setEditing(NEW_KEY); setForm(emptyForm()); }} className="flex items-center gap-1 text-xs font-sans text-saffron font-semibold hover:text-saffron-dark" data-testid="add-seo-page"><Plus size={12} /> Custom Page</button>
      </div>
      {allPaths.map(path => {
        const def = DEFAULT_PAGES.find(p => p.path === path);
        const isEditing = editing === path;
        return (
          <div key={path} className={`bg-white rounded-lg border p-4 transition-all ${isEditing ? 'border-saffron shadow-saffron-glow' : 'border-saffron-100/50'}`} data-testid={`seo-page-${path.replace(/\//g, '-')}`}>
            {isEditing ? (
              <SEOEditForm {...formProps} />
            ) : (
              <SEOPageSummary path={path} label={def?.label} setting={settingsMap[path]} isDefault={Boolean(def)} onEdit={() => startEdit(path)} onDelete={() => remove(path)} />
            )}
          </div>
        );
      })}
      {editing === NEW_KEY && (
        <div className="bg-white rounded-lg border border-saffron shadow-saffron-glow p-4" data-testid="seo-new-form">
          <SEOEditForm {...formProps} isNew />
        </div>
      )}
    </div>
  );
}
